import { db, storage } from "./config"
import { collection, addDoc, getDocs, query, where, doc, updateDoc, getDoc, deleteDoc } from "firebase/firestore"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import { addNotification } from "./notifications"

export const createListing = async ({ userId, username, pictures, title, condition, subCategory, releasedYear, quantity, price, category, description, brand, boxType, upcCodes, sellerSKU, originalPrice, paymentMethods }) => {
    try {
        // Reference to the "listings" collection
        const listingRef = collection(db, "listings")
        const imageURLs = []

        for (const [index, image] of pictures.entries()) {
            const storageRef = ref(storage, `images/listings/${title}-${index + 1}`);
            await uploadBytes(storageRef, image);
            const imageURL = await getDownloadURL(storageRef);
            imageURLs.push(imageURL);
        }

        // Add a new document with a generated ID
        const docRef = await addDoc(listingRef, {
            seller: userId,
            sellerUserName: username,
            pictures: imageURLs,
            title: title,
            condition: condition,
            price: parseInt(price),
            quantity: parseInt(quantity),
            released_year: parseInt(releasedYear),
            brand: brand,
            rarity: boxType,
            condition: condition,
            upcCodes: upcCodes,
            sellerSKU: sellerSKU,
            category: category,
            sub_category: subCategory,
            description: description,
            state: 'pending',
            created_at: new Date(),
            original_price: parseInt(originalPrice),
            paymentMethods: paymentMethods || []
        })

        console.log("Document written with ID: ", docRef.id)
    } catch (e) {
        console.error("Error adding document: ", e)
    }
}

export const updateListing = async ({ userId, username, cardId, pictures, title, condition, subCategory, releasedYear, quantity, price, category, description, brand, boxType, originalPrice, paymentMethods }) => {
    try {
        // Reference to the "listings" collection
        const listingsRef = doc(db, "listings", cardId)
        const imageURLs = []

        for (const [index, image] of pictures.entries()) {
            if (typeof image === 'string') {
                imageURLs.push(image);
            } else if (image) {
                const storageRef = ref(storage, `images/listings/${title}-${index + 1}`);
                await uploadBytes(storageRef, image);
                const imageURL = await getDownloadURL(storageRef);
                imageURLs.push(imageURL);
            }
        }

        // Prepare update data
        const newData = {
            seller: userId,
            sellerUserName: username,
            pictures: imageURLs,
            title: title,
            condition: condition,
            price: parseInt(price),
            quantity: parseInt(quantity),
            released_year: parseInt(releasedYear),
            brand: brand,
            rarity: boxType,
            category: category,
            sub_category: subCategory,
            description: description,
            state: 'pending',
            original_price: parseInt(originalPrice),
            paymentMethods: paymentMethods || []
        }

        // Update the document
        await updateDoc(listingsRef, newData)
        console.log("Document updated successfully")
        return true
    } catch (e) {
        console.error("Error updating document: ", e)
        throw e
    }
}

export const deleteListing = async (cardId) => {
    try {
        // Reference to the specific category document
        const listingsRef = doc(db, "listings", cardId);

        // Delete the document
        await deleteDoc(listingsRef);

        console.log("Document deleted with ID: ", cardId);
    } catch (e) {
        console.error("Error deleting document: ", e);
    }
}

export const updateCardState = async (cardId, state) => {
    try {
        // Reference to the specific card document in the "listings" collection
        const cardRef = doc(db, "listings", cardId);

        // Update the state of the card to 'local'
        await updateDoc(cardRef, { state: state });
    } catch (e) {
        console.error("Error updating card state: ", e);
    }
};


export const getAllListings = async (userId) => {
    try {
        // Reference to the "listings" collection
        const listingsRef = collection(db, "listings")

        const listings = []

        // Create a query to filter listings by state
        const q = query(
            listingsRef,
            where('state', '==', 'approved')
        )
        const querySnapshot = await getDocs(q)
        querySnapshot.forEach((doc) => {
            listings.push({ id: doc.id, ...doc.data() })
        })
        listings.sort((a, b) => b.created_at - a.created_at);

        return listings // Return the array of listings
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getAllListingsAndUpdateState = async () => {
    try {
        // Reference to the "listings" collection
        const listingsRef = collection(db, "listings");

        // Create a query to retrieve all listings (you can adjust this if needed)
        const q = query(listingsRef);
        const querySnapshot = await getDocs(q);

        const listings = [];

        // Iterate over each listing document
        querySnapshot.forEach((doc) => {
            listings.push({ id: doc.id, ...doc.data() });
        });

        // Update each listing's state to 'approved'
        const updatePromises = listings.map(async (listing) => {
            const listingRef = doc(db, "listings", listing.id);
            await updateDoc(listingRef, { state: 'approved' });
        });

        // Wait for all updates to complete
        await Promise.all(updatePromises);

        // Optionally, sort the listings after updates
        listings.sort((a, b) => b.created_at - a.created_at);

        return listings; // Return the array of listings
    } catch (e) {
        console.error("Error getting or updating documents: ", e);
        return []; // Return an empty array in case of error
    }
}

export const getSearchedListings = async (userId, search) => {
    try {
        // Reference to the "listings" collection
        const listingsRef = collection(db, "listings")

        const listings = []

        // Create a query to filter listings by state
        const q = query(
            listingsRef,
            where('seller', '!=', userId),
            where('state', '==', 'market')
        )
        const querySnapshot = await getDocs(q)
        querySnapshot.forEach((doc) => {
            doc.data().title.includes(search) && listings.push({ id: doc.id, ...doc.data() })
        })

        return listings // Return the array of listings
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getListingsByCategories = async (categories, userId) => {
    try {
        // Reference to the "listings" collection
        const listingsRef = collection(db, "listings")

        const listings = []

        for (let category of categories) {
            const q = query(
                listingsRef,
                where("category", "==", category),
                where("seller", '!=', userId),
                where('state', '==', 'market')
            )
            const querySnapshot = await getDocs(q)
            querySnapshot.forEach((doc) => {
                if (category.subCategories.includes(doc.data().sub_category))
                    listings.push({ id: doc.id, ...doc.data() })
            })
        }

        return listings // Return the array of listings
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getListingsByBrands = async (brands, userId) => {
    try {
        // Reference to the "listings" collection
        const listingsRef = collection(db, "listings")

        const listings = []

        for (let brand of brands) {
            const q = query(
                listingsRef,
                where("brand", "==", brand),
                where("seller", '!=', userId),
                where('state', '==', 'market')
            )
            const querySnapshot = await getDocs(q)
            querySnapshot.forEach((doc) => {
                listings.push({ id: doc.id, ...doc.data() })
            })
        }

        return listings // Return the array of listings
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getListingsByConditions = async (conditions, userId) => {
    try {
        // Reference to the "listings" collection
        const listingsRef = collection(db, "listings")

        const listings = []

        for (let condition of conditions) {
            const q = query(
                listingsRef,
                where("condition", "==", condition),
                where("seller", '!=', userId),
                where('state', '==', 'market')
            )
            const querySnapshot = await getDocs(q)
            querySnapshot.forEach((doc) => {
                listings.push({ id: doc.id, ...doc.data() })
            })
        }

        return listings // Return the array of listings
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getListingsByRarities = async (rarities, userId) => {
    try {
        // Reference to the "listings" collection
        const listingsRef = collection(db, "listings")

        const listings = []

        for (let rarity of rarities) {
            const q = query(
                listingsRef,
                where("rarity", "==", rarity),
                where("seller", '!=', userId),
                where('state', '==', 'market')
            )
            const querySnapshot = await getDocs(q)
            querySnapshot.forEach((doc) => {
                listings.push({ id: doc.id, ...doc.data() })
            })
        }

        return listings // Return the array of listings
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getListingsByPrice = async (value, userId) => {
    try {
        // Reference to the "listings" collection
        const listingsRef = collection(db, "listings")

        const listings = []

        const q = query(
            listingsRef,
            where("price", ">=", value.min),
            where("price", "<=", value.max),
            where("seller", '!=', userId),
            where('state', '==', 'market')
        )
        const querySnapshot = await getDocs(q)
        querySnapshot.forEach((doc) => {
            listings.push({ id: doc.id, ...doc.data() })
        })

        return listings // Return the array of listings
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getListingsByUserId = async (userId) => {
    try {
        // Reference to the "listings" collection
        const listingsRef = collection(db, "listings")

        const listings = []

        // Create a query to filter listings by sellerId
        const q = query(listingsRef, where("seller", "==", userId))
        const querySnapshot = await getDocs(q)

        querySnapshot.forEach((doc) => {
            listings.push({ id: doc.id, ...doc.data() })
        })

        listings.sort((a, b) => b.created_at - a.created_at);

        return listings // Return the array of listings
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getListingsByUserIdAndState = async (userId, state) => {
    try {
        // Reference to the "listings" collection
        const listingsRef = collection(db, "listings")

        const listings = []

        // Create a query to filter listings by sellerId
        const q = query(listingsRef,
            where("seller", "==", userId),
            where("state", "==", state)
        )
        const querySnapshot = await getDocs(q)

        querySnapshot.forEach((doc) => {
            listings.push({ id: doc.id, ...doc.data() })
        })

        listings.sort((a, b) => b.created_at - a.created_at);

        return listings // Return the array of listings
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getListingByID = async (id) => {
    try {
        // Reference to the specific document in the "listings" collection
        const listingRef = doc(db, "listings", id)

        // Retrieve the document snapshot
        const docSnapshot = await getDoc(listingRef)

        // Check if the document exists
        if (docSnapshot.exists()) {
            return { id: docSnapshot.id, ...docSnapshot.data() } // Return the listing data
        } else {
            console.error("No such document!")
            return null // Return null if the document does not exist
        }
    } catch (e) {
        console.error("Error getting document: ", e)
        return null // Return null in case of error
    }
}

export const addToCart = async (userId, cartedItem) => {
    try {
        // Reference to the specific user document
        const usersRef = doc(db, "users", userId)
        const userSnapshot = await getDoc(usersRef)
        const userData = userSnapshot.data()
        const oldCart = userData.cartList ? userData.cartList : []
        if (oldCart.findIndex(item => item.id === cartedItem.id) === -1) {
            const newUserData = { ...userData, cartList: [...oldCart, cartedItem] }
            await updateDoc(usersRef, newUserData)
        }
    } catch (e) {
        console.error("Error getting document: ", e)
    }
}

export const removeFromCart = async (userId, cartId) => {
    try {
        // Reference to the specific user document
        const usersRef = doc(db, "users", userId)
        const userSnapshot = await getDoc(usersRef)
        const userData = userSnapshot.data()
        const oldCart = userData.cartList ? userData.cartList : []
        const newCart = oldCart.reduce((acc, cur) => {
            if (cur.id !== cartId)
                acc.push(cur)
            return acc
        }, [])

        const newUserData = { ...userData, cartList: newCart }

        await updateDoc(usersRef, newUserData)
    } catch (e) {
        console.error("Error getting document: ", e)
    }
}

export const isListingInCart = async (userId, listingId) => {
    try {
        // Reference to the specific user document
        const usersRef = doc(db, "users", userId)
        const userSnapshot = await getDoc(usersRef)
        const userData = userSnapshot.data()
        const oldCart = userData.cartList ? userData.cartList : []
        if (oldCart.findIndex(item => item.id === listingId) > -1) {
            return true
        } else {
            return false
        }
    } catch (e) {
        console.error("Error getting document: ", e)
    }
}

export const buyListing = async (userId, card) => {
    try {
        // Reference to the specific user document
        const usersRef = doc(db, "users", userId)
        const userSnapshot = await getDoc(usersRef)
        const userData = userSnapshot.data()

        const listingsRef = doc(db, "listings", card.id)
        const listingSnapshot = await getDoc(listingsRef)
        const listingData = listingSnapshot.data()

        const newUserData = { ...userData, balance: userData.balance - card.price }
        await updateDoc(listingsRef, { ...listingData, state: "pending", buyer: userId })
        await updateDoc(usersRef, newUserData)
    } catch (e) {
        console.error("Error getting document: ", e)
    }
}

export const cancelBuyListing = async (card) => {
    try {
        // Reference to the specific user document
        const usersRef = doc(db, "users", card.buyer)
        const userSnapshot = await getDoc(usersRef)
        const userData = userSnapshot.data()

        const listingsRef = doc(db, "listings", card.id)
        const listingSnapshot = await getDoc(listingsRef)
        const listingData = listingSnapshot.data()

        const newUserData = { ...userData, balance: userData.balance + card.price }
        await updateDoc(listingsRef, { ...listingData, state: "market", buyer: null })
        await updateDoc(usersRef, newUserData)
    } catch (e) {
        console.error("Error getting document: ", e)
    }
}

export const acceptOnSellerSide = async (card) => {
    try {
        const listingsRef = doc(db, "listings", card.id)
        const listingSnapshot = await getDoc(listingsRef)
        const listingData = listingSnapshot.data()

        await updateDoc(listingsRef, { ...listingData, state: "delivering" })
    } catch (e) {
        console.error("Error getting document: ", e)
    }
}

export const acceptOnBuyerSide = async (card) => {
    try {
        const listingsRef = doc(db, "listings", card.id)
        const listingSnapshot = await getDoc(listingsRef)
        const listingData = listingSnapshot.data()

        // Update listing state to "delivering" without updating seller balance
        await updateDoc(listingsRef, { ...listingData, state: "delivering" })

        const buyerRef = doc(db, "users", card.buyer)
        const buyerSnapshot = await getDoc(buyerRef)
        const buyerData = buyerSnapshot.data()
        const purchases = buyerData.purchases ? buyerData.purchases + 1 : 1

        const newBuyerData = { ...buyerData, purchases }
        await updateDoc(buyerRef, newBuyerData)

        // Send notification to seller about the purchase
        try {
            const sellerNotificationData = {
                type: 'purchase_completed',
                action: 'purchased',
                listingId: card.id,
                listingTitle: card.title,
                recipientId: card.seller, // Notify the seller
                message: `Your listing "${card.title}" has been purchased for $${card.price.toFixed(2)}. Payment will be released when delivery is confirmed.`,
                timestamp: new Date(),
                isRead: false
            };
            
            await addNotification(sellerNotificationData);
            console.log('Purchase notification sent to seller:', card.seller);
        } catch (notificationError) {
            console.error('Error sending purchase notification to seller:', notificationError);
        }

        // Send notification to buyer about the purchase
        try {
            const buyerNotificationData = {
                type: 'purchase_completed',
                action: 'purchased',
                listingId: card.id,
                listingTitle: card.title,
                recipientId: card.buyer, // Notify the buyer
                message: `You have successfully purchased "${card.title}" for $${card.price.toFixed(2)}. Please confirm delivery when you receive the item.`,
                timestamp: new Date(),
                isRead: false
            };
            
            await addNotification(buyerNotificationData);
            console.log('Purchase notification sent to buyer:', card.buyer);
        } catch (notificationError) {
            console.error('Error sending purchase notification to buyer:', notificationError);
        }
    } catch (e) {
        console.error("Error getting document: ", e)
    }
}
